/**
 * Nationality options — mirrors iOS Nationality enum (UserSettingsService).
 * Stored value is the display name (rawValue); iso2 is for Country Info / visa.
 */

export interface NationalityOption {
  /** Value stored in appUserSettings.userNationality (iOS rawValue). */
  value: string;
  iso2: string;
}

export const NATIONALITY_OPTIONS: NationalityOption[] = [
  { value: 'Argentina', iso2: 'AR' },
  { value: 'Australia', iso2: 'AU' },
  { value: 'Austria', iso2: 'AT' },
  { value: 'Belgium', iso2: 'BE' },
  { value: 'Brazil', iso2: 'BR' },
  { value: 'Canada', iso2: 'CA' },
  { value: 'Chile', iso2: 'CL' },
  { value: 'China', iso2: 'CN' },
  { value: 'Colombia', iso2: 'CO' },
  { value: 'Czech Republic', iso2: 'CZ' },
  { value: 'Denmark', iso2: 'DK' },
  { value: 'Egypt', iso2: 'EG' },
  { value: 'Finland', iso2: 'FI' },
  { value: 'France', iso2: 'FR' },
  { value: 'Germany', iso2: 'DE' },
  { value: 'Ghana', iso2: 'GH' },
  { value: 'Greece', iso2: 'GR' },
  { value: 'Hungary', iso2: 'HU' },
  { value: 'India', iso2: 'IN' },
  { value: 'Indonesia', iso2: 'ID' },
  { value: 'Israel', iso2: 'IL' },
  { value: 'Italy', iso2: 'IT' },
  { value: 'Japan', iso2: 'JP' },
  { value: 'Kenya', iso2: 'KE' },
  { value: 'Malaysia', iso2: 'MY' },
  { value: 'Mexico', iso2: 'MX' },
  { value: 'Morocco', iso2: 'MA' },
  { value: 'Netherlands', iso2: 'NL' },
  { value: 'New Zealand', iso2: 'NZ' },
  { value: 'Nigeria', iso2: 'NG' },
  { value: 'Norway', iso2: 'NO' },
  { value: 'Other', iso2: '' },
  { value: 'Peru', iso2: 'PE' },
  { value: 'Philippines', iso2: 'PH' },
  { value: 'Poland', iso2: 'PL' },
  { value: 'Portugal', iso2: 'PT' },
  { value: 'Singapore', iso2: 'SG' },
  { value: 'South Africa', iso2: 'ZA' },
  { value: 'South Korea', iso2: 'KR' },
  { value: 'Spain', iso2: 'ES' },
  { value: 'Sweden', iso2: 'SE' },
  { value: 'Switzerland', iso2: 'CH' },
  { value: 'Thailand', iso2: 'TH' },
  { value: 'Turkey', iso2: 'TR' },
  { value: 'USA', iso2: 'US' },
  { value: 'United Kingdom', iso2: 'GB' },
  { value: 'Venezuela', iso2: 'VE' },
  { value: 'Vietnam', iso2: 'VN' },
].sort((a, b) => a.value.localeCompare(b.value));

const byValue = new Map(NATIONALITY_OPTIONS.map((o) => [o.value.toLowerCase(), o]));
const byIso2 = new Map(
  NATIONALITY_OPTIONS.filter((o) => o.iso2).map((o) => [o.iso2.toUpperCase(), o])
);

/** Resolve stored nationality (name or ISO2) to a 2-letter citizen code. */
export function nationalityToIso2(value: string | null | undefined): string | null {
  if (!value?.trim()) return null;
  const raw = value.trim();
  if (/^[A-Za-z]{2}$/.test(raw)) {
    const upper = raw.toUpperCase();
    return byIso2.has(upper) || upper.length === 2 ? upper : null;
  }
  const hit = byValue.get(raw.toLowerCase());
  if (!hit?.iso2) return null;
  return hit.iso2;
}
